import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { applyWhen, disabled, form, max, maxLength, min, minLength, required, validate } from "@angular/forms/signals";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DspAdvertiserDeal,
  DspAdvertiserDealDealTypeEnum,
  StrategyDspAudiencePreset,
  StrategyDspCreativeType,
  StrategyDspFunnel,
  StrategyStateEnum,
} from "@front/m19-api-client";
import {
  AudienceId,
  Constant,
  dealAcceptsDuration,
  findDealWithoutAudienceTargeting,
  DspAudiencesConfig,
  DspCreateStrategy,
  DspStrategyService,
} from "@front/m19-services";
import { IButtonDirective, IStepperComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { of, switchMap, take } from "rxjs";
import { DspFormService } from "@m19-board/dsp/dsp-create-strategy/dsp-form.service";
import {
  DspCreateStrategyAsinsModel,
  DspCreateStrategyAsinsStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-asins-step.component";
import {
  DspCreateStrategyAudiencesModel,
  DspCreateStrategyAudiencesStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-audiences-step.component";
import {
  DspCreateStrategyBudgetModel,
  DspCreateStrategyBudgetStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-budget-step.component";
import {
  DspCreateStrategyCreativeModel,
  DspCreateStrategyCreativeStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-creative-step.component";
import {
  DspCreateStrategyMediaModel,
  DspCreateStrategyMediaStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-media-step.component";
import {
  DspCreateStrategySetupModel,
  DspCreateStrategySetupStepComponent,
} from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-setup-step.component";
import { DspSmartAudiencesService } from "@m19-board/dsp/dsp-smart-audiences-service/dsp-smart-audiences.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import { validateCompleteDateRange } from "@m19-board/utils/validators/dateRangeValidators";
import { validateStrategyDspAudiences } from "@m19-board/utils/validators/dspAudiencesValidators";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";

interface DspCreateStrategyFormModel {
  setup: DspCreateStrategySetupModel;
  media: DspCreateStrategyMediaModel;
  budget: DspCreateStrategyBudgetModel;
  creative: DspCreateStrategyCreativeModel;
  audiences: DspCreateStrategyAudiencesModel;
  asins: DspCreateStrategyAsinsModel;
}

const VIDEO_CREATIVE_TYPES: StrategyDspCreativeType[] = [
  StrategyDspCreativeType.VIDEO,
  StrategyDspCreativeType.STREAMING_TV,
  StrategyDspCreativeType.VIDEO_DEAL,
];

@Component({
  selector: "app-dsp-create-strategy",
  providers: [DspFormService, FormNavigationService],
  imports: [
    FormShellComponent,
    IStepperComponent,
    DspCreateStrategySetupStepComponent,
    DspCreateStrategyMediaStepComponent,
    DspCreateStrategyBudgetStepComponent,
    DspCreateStrategyCreativeStepComponent,
    DspCreateStrategyAudiencesStepComponent,
    DspCreateStrategyAsinsStepComponent,
    IButtonDirective,
    TranslocoDirective,
  ],
  templateUrl: "./dsp-create-strategy.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCreateStrategyComponent {
  private readonly dspFormService = inject(DspFormService);
  private readonly strategyService = inject(DspStrategyService);
  private readonly dspSmartAudiencesService = inject(DspSmartAudiencesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formNavigationService = inject(FormNavigationService<DspCreateStrategyFormModel>);

  private readonly formModel = signal<DspCreateStrategyFormModel>({
    setup: {
      name: "",
      agencyFee: null,
      dspFunnel: null,
    },
    media: {
      creativeType: null,
      dealId: null,
    },
    budget: {
      budget: null,
      budgetDates: null,
      monthlyBudget: null,
    },
    creative: {
      creativeType: null,
      videoAssetId: null,
    },
    audiences: {
      audiencePreset: StrategyDspAudiencePreset.MANUAL,
      audiences: [[]],
      blacklistedAudiences: [[]],
    },
    asins: {
      asins: [],
    },
  });

  private readonly videoDeals = toSignal(this.dspFormService.videoDeals$, { initialValue: [] });
  readonly uploadedVideoDurationSeconds = signal<number | undefined>(undefined);
  readonly strategyCreationState = signal<"idle" | "creating-audiences" | "creating-strategy">("idle");
  readonly isCreatingStrategy = computed(() => this.strategyCreationState() !== "idle");

  readonly creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.setup.name);
      maxLength(schema.setup.name, Constant.strategyNameMaxLength);
      required(schema.setup.dspFunnel);
      min(schema.setup.agencyFee, DspStrategyService.AGENCY_FEE_MIN);
      max(schema.setup.agencyFee, DspStrategyService.AGENCY_FEE_MAX);
      required(schema.asins.asins);
      minLength(schema.asins.asins, 1);
      applyWhen(
        schema,
        ({ valueOf }) => valueOf(schema.setup.dspFunnel) === StrategyDspFunnel.AWARENESS,
        (awarenessSchema) => {
          required(awarenessSchema.media.creativeType);
          required(awarenessSchema.media.dealId, {
            when: ({ valueOf }) => valueOf(awarenessSchema.media.creativeType) === StrategyDspCreativeType.VIDEO_DEAL,
          });
          required(awarenessSchema.budget.budget);
          required(awarenessSchema.budget.budgetDates);
          validate(awarenessSchema.budget.budgetDates, validateCompleteDateRange);
          min(awarenessSchema.budget.budget, 1);
        },
      );
      applyWhen(
        schema,
        ({ valueOf }) => {
          const funnel = valueOf(schema.setup.dspFunnel);
          return funnel === StrategyDspFunnel.CONVERSION || funnel === StrategyDspFunnel.CONSIDERATION;
        },
        (nonAwarenessSchema) => {
          required(nonAwarenessSchema.budget.monthlyBudget);
          min(nonAwarenessSchema.budget.monthlyBudget, 1);
          required(nonAwarenessSchema.creative.creativeType);
        },
      );
      required(schema.creative.videoAssetId, {
        when: ({ valueOf }) => {
          const funnel = valueOf(schema.setup.dspFunnel);
          const creativeType =
            funnel === StrategyDspFunnel.AWARENESS
              ? valueOf(schema.media.creativeType)
              : valueOf(schema.creative.creativeType);
          return !!creativeType && VIDEO_CREATIVE_TYPES.includes(creativeType);
        },
      });
      applyWhen(
        schema,
        ({ valueOf }) =>
          valueOf(schema.setup.dspFunnel) !== StrategyDspFunnel.AWARENESS &&
          valueOf(schema.audiences.audiencePreset) === StrategyDspAudiencePreset.MANUAL,
        (manualAudiencesSchema) => {
          required(manualAudiencesSchema.audiences.audiences);
          validate(manualAudiencesSchema.audiences.audiences, validateStrategyDspAudiences);
        },
      );
      applyWhen(
        schema,
        ({ valueOf }) => !!this.guaranteedDeal(valueOf(schema.media.dealId)),
        (schema) => {
          disabled(schema.budget.budget);
          disabled(schema.budget.budgetDates);
        },
      );
    },
    {
      submission: {
        action: async () => this.submit(),
      },
    },
  );

  private readonly selectedGuaranteedDeal = computed(() =>
    this.guaranteedDeal(this.creationForm.media.dealId().value()),
  );

  private readonly selectedDealForbidsAudiences = computed(() => {
    const dealId = this.creationForm.media.dealId().value();
    return !!findDealWithoutAudienceTargeting(this.videoDeals(), dealId ? [dealId] : []);
  });

  private readonly effectiveCreativeType = computed(() => {
    const funnel = this.creationForm.setup.dspFunnel().value();
    return funnel === StrategyDspFunnel.AWARENESS
      ? this.creationForm.media.creativeType().value()
      : this.creationForm.creative.creativeType().value();
  });

  private readonly isVideoDeal = computed(() => this.effectiveCreativeType() === StrategyDspCreativeType.VIDEO_DEAL);

  private readonly withVideoSection = computed(() => {
    const funnel = this.creationForm.setup.dspFunnel().value();
    const creativeType = this.effectiveCreativeType();
    return funnel === StrategyDspFunnel.AWARENESS || (!!creativeType && VIDEO_CREATIVE_TYPES.includes(creativeType));
  });

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;
  readonly StrategyDspAudiencePreset = StrategyDspAudiencePreset;

  readonly showMedia = computed(() => this.creationForm.setup.dspFunnel().value() === StrategyDspFunnel.AWARENESS);

  readonly showBudget = computed(() => {
    return (
      this.creationForm.setup.dspFunnel().value() !== StrategyDspFunnel.AWARENESS || !this.selectedGuaranteedDeal()
    );
  });

  readonly showAudiences = computed(() => {
    const funnel = this.creationForm.setup.dspFunnel().value();
    const creativeType = this.creationForm.media.creativeType().value();
    return (
      !this.selectedDealForbidsAudiences() &&
      (funnel !== StrategyDspFunnel.AWARENESS || creativeType === StrategyDspCreativeType.VIDEO_DEAL)
    );
  });

  constructor() {
    this.formNavigationService.configure({
      steps: [
        {
          stepId: "setup",
          label: this.translocoService.translate("common.info"),
          size: "sm",
        },
        {
          stepId: "media",
          label: this.translocoService.translate("dsp-media-selector.media"),
          size: "sm",
          showWhen: this.showMedia,
        },
        {
          stepId: "budget",
          label: this.translocoService.translate("common.budget"),
          size: "sm",
          showWhen: this.showBudget,
        },
        {
          stepId: "creative",
          label: this.translocoService.translate("common.creatives"),
          size: "sm",
        },
        {
          stepId: "audiences",
          label: this.translocoService.translate("common.audiences"),
          size: "md",
          showWhen: this.showAudiences,
        },
        {
          stepId: "asins",
          label: this.translocoService.translate("common.asins"),
          size: "md",
        },
      ],
      form: this.creationForm,
      blocked: this.isCreatingStrategy,
    });

    effect(() => {
      const funnel = this.creationForm.setup.dspFunnel().value();
      untracked(() => {
        this.formNavigationService.updateReachedStepIds(["setup"]);
        this.creationForm.budget.monthlyBudget().reset(null);
        this.creationForm.budget.budget().reset(null);
        this.creationForm.budget.budgetDates().reset(null);

        if (funnel === StrategyDspFunnel.AWARENESS) {
          this.creationForm.creative.videoAssetId().markAsDirty();
        }

        if (this.creationForm.audiences.audiencePreset().value() !== StrategyDspAudiencePreset.MANUAL) {
          this.creationForm.audiences.audiencePreset().value.set(StrategyDspAudiencePreset.MANUAL);
        }
        if (this.creationForm.creative.videoAssetId().value() !== null) {
          this.creationForm.creative.videoAssetId().reset(null);
        }
        this.uploadedVideoDurationSeconds.set(undefined);
        if (funnel === StrategyDspFunnel.AWARENESS || !funnel) return;
        if (this.creationForm.media.creativeType().value() !== null) {
          this.creationForm.media.creativeType().reset(null);
        }
        if (this.creationForm.media.dealId().value() !== null) {
          this.creationForm.media.dealId().reset(null);
        }
      });
    });
  }

  goToStep(stepId: IFormStep<DspCreateStrategyFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  submit() {
    if (this.creationForm().invalid() || this.isCreatingStrategy()) return;

    const asinsToCreate = this.creationForm.asins.asins().value();
    if (asinsToCreate.length > this.strategyService.maxAsinsPerStrategy) {
      this.toasterService.error("You can only select up to " + this.strategyService.maxAsinsPerStrategy + " products");
      return;
    }

    const funnel = this.creationForm.setup.dspFunnel().value();
    if (funnel === StrategyDspFunnel.AWARENESS) {
      if (this.isVideoDeal() && !this.isVideoDurationCompatibleWithDeals()) {
        this.toasterService.error(
          this.translocoService.translate("dsp-create-strategy-page.video_duration_deal_mismatch"),
        );
        return;
      }
      this.createAwarenessStrategy(asinsToCreate);
    } else {
      this.createConversionOrConsiderationStrategy(asinsToCreate);
    }
  }

  private createConversionOrConsiderationStrategy(asinsToCreate: string[]) {
    this.strategyCreationState.set("creating-audiences");

    this.maybeCreateAudiences$(asinsToCreate)
      .pipe(
        take(1),
        switchMap((audiences) => {
          this.strategyCreationState.set("creating-strategy");
          const strategyToCreate: DspCreateStrategy = {
            monthlyBudget: this.creationForm.budget.monthlyBudget().value()!,
            name: this.creationForm.setup.name().value(),
            state: StrategyStateEnum.ENABLED,
            asins: asinsToCreate,
            dspFunnel: this.creationForm.setup.dspFunnel().value()!,
            creativeType: this.creationForm.creative.creativeType().value()!,
            audiencePreset: this.creationForm.audiences.audiencePreset().value(),
            videoAssetId: this.withVideoSection()
              ? (this.creationForm.creative.videoAssetId().value() ?? undefined)
              : undefined,
            agencyFee: this.creationForm.setup.agencyFee().value() || undefined,
          };

          return this.strategyService.createDspStrategy(
            strategyToCreate,
            audiences.whitelistAudiences,
            audiences.blacklistAudiences,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (s) => {
          this.router.navigate(["..", s.strategyId], {
            relativeTo: this.route,
            queryParamsHandling: "merge",
          });
        },
        error: (err) => {
          this.toasterService.error(err);
          this.strategyCreationState.set("idle");
        },
      });
  }

  private createAwarenessStrategy(asinsToCreate: string[]) {
    this.strategyCreationState.set("creating-strategy");
    const budgetDates = this.creationForm.budget.budgetDates().value()!;
    const creativeType = this.creationForm.media.creativeType().value()!;

    const strategyToCreate: DspCreateStrategy = {
      budgetFlight: {
        budget: this.creationForm.budget.budget().value()!,
        startDate: budgetDates.start!.toISODate()!,
        endDate: budgetDates.end!.toISODate()!,
      },
      name: this.creationForm.setup.name().value(),
      state: StrategyStateEnum.ENABLED,
      asins: asinsToCreate,
      dspFunnel: StrategyDspFunnel.AWARENESS,
      creativeType,
      videoAssetId: this.creationForm.creative.videoAssetId().value()!,
      dealIds: this.isVideoDeal() ? [this.creationForm.media.dealId().value()!] : undefined,
      agencyFee: this.creationForm.setup.agencyFee().value() || undefined,
    };

    let audiences: DspAudiencesConfig = { whitelistAudiences: [], blacklistAudiences: [] };
    if (creativeType === StrategyDspCreativeType.VIDEO_DEAL) {
      strategyToCreate.audiencePreset = StrategyDspAudiencePreset.MANUAL;
      if (!this.selectedDealForbidsAudiences()) {
        audiences = this.getManualAudiencesConfig();
      }
    }

    this.strategyService
      .createDspStrategy(strategyToCreate, audiences.whitelistAudiences, audiences.blacklistAudiences)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => {
          this.router.navigate(["..", s.strategyId], {
            relativeTo: this.route,
            queryParamsHandling: "merge",
          });
        },
        error: (err) => {
          this.toasterService.error(err);
          this.strategyCreationState.set("idle");
        },
      });
  }

  private maybeCreateAudiences$(asinsToCreate: string[]) {
    const audiencePreset = this.creationForm.audiences.audiencePreset().value();
    return audiencePreset === StrategyDspAudiencePreset.MANUAL
      ? of(this.getManualAudiencesConfig())
      : this.dspSmartAudiencesService.createAudiencesFromSmartAudiencePreset(
          this.creationForm.setup.name().value(),
          asinsToCreate,
          audiencePreset,
        );
  }

  private getManualAudiencesConfig(): DspAudiencesConfig {
    const audiencesToCreate =
      this.creationForm.audiences
        .audiences()
        .value()
        ?.filter((audience): audience is AudienceId[] => audience !== null) ?? [];
    const blacklistedAudiencesToCreate =
      this.creationForm.audiences
        .blacklistedAudiences()
        .value()
        ?.filter((audience): audience is AudienceId[] => audience !== null && audience.length > 0) ?? [];

    return {
      whitelistAudiences: audiencesToCreate,
      blacklistAudiences: blacklistedAudiencesToCreate,
    };
  }

  private isVideoDurationCompatibleWithDeals(): boolean {
    const duration = this.uploadedVideoDurationSeconds();
    if (duration === undefined) return true;
    const dealId = this.creationForm.media.dealId().value();
    const selectedDeals = this.videoDeals().filter((deal) => dealId === deal.dealId);
    return selectedDeals.every((deal) => dealAcceptsDuration(deal, duration));
  }

  private guaranteedDeal(dealId: string | null): DspAdvertiserDeal | undefined {
    const deal = dealId ? this.videoDeals().find((d) => d.dealId === dealId) : undefined;
    return deal?.dealType === DspAdvertiserDealDealTypeEnum.PROGRAMMATIC_GUARANTEED ? deal : undefined;
  }
}
